import { useQuery } from '@tanstack/react-query';

import { fbmSubstitutionRulesV1Api } from 'api/fbmSubstitutionRulesV1';
import { ApiError } from 'shared/errors/ApiError';

export const SUBSTITUTION_RULES_QUERY_KEY = 'fbmSubstitutionRules';

export const substitutionRulesQueryKeys = {
    all: [SUBSTITUTION_RULES_QUERY_KEY],
    account: (fbmAccountName) => [SUBSTITUTION_RULES_QUERY_KEY, fbmAccountName],
    list: (fbmAccountName, facilityId, week, year) => {
        const key = [SUBSTITUTION_RULES_QUERY_KEY, fbmAccountName, facilityId];
        if (week != null && year != null) {
            key.push(week, year);
        }
        return key;
    },
};

/**
 * Fetches substitution rules for an FBM account.
 *
 * @param {Object} options
 * @param {string} [options.fbmAccountName] - FBM account name (from user context)
 * @param {number|string} [options.facilityId] - Facility ID
 * @param {number} [options.week] - Pack week
 * @param {number} [options.year] - Pack year
 * @param {boolean} [options.enabled=true] - Whether the query should run
 */
export function useSubstitutionRulesQuery({ enabled = true, facilityId, fbmAccountName, week, year } = {}) {
    return useQuery({
        enabled: enabled && !!fbmAccountName && week != null && year != null,
        queryFn: async () => {
            const response = await fbmSubstitutionRulesV1Api.getRules(fbmAccountName, facilityId, week, year);
            if (!response?.status) {
                throw new ApiError(
                    response?.msg ?? 'Failed to fetch substitution rules',
                    response?.statusCode ?? 400,
                    response?.data,
                );
            }
            return response.data?.rows ?? [];
        },
        queryKey: substitutionRulesQueryKeys.list(fbmAccountName, facilityId, week, year),
        retry: (failureCount, err) => {
            if (err instanceof ApiError && (err.isServerError || err.isAuthError || err.isClientError)) {
                return false;
            }
            return failureCount < 2;
        },
        staleTime: 60_000,
    });
}
